/*!
 * Saigu Cosmetics · Test de visagismo
 * Análisis facial en el navegador con MediaPipe Face Landmarker.
 * La foto nunca sale del dispositivo de la clienta: no hay servidor ni subida.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * 1. CONFIGURACIÓN
   * ------------------------------------------------------------------ */
  var MP_VERSION = '0.10.14';
  var DEFAULTS = {
    moduleUrl: 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@' + MP_VERSION + '/vision_bundle.mjs',
    wasmBase: 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@' + MP_VERSION + '/wasm',
    modelUrl: 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task'
  };

  /*
   * Calibración (ajustada con 8 fotos etiquetadas por Saigu, 27/10/2026;
   * recalibrada el 30/10/2026 con 13 fotos etiquetadas y el 03/11/2026 con 15).
   * Cada regla tiene un punto de corte «c» y una escala «s»: la puntuación es
   * (medida - c) / s, así que 0 es la frontera entre dos tipos.
   * El modo ?visagismo_debug=1 muestra las medidas de cada foto para seguir afinando.
   */
  var RULES = {
    jawStrong: { c: 0.805, s: 0.01 }, // ancho de mandíbula / ancho de pómulos: por encima, mandíbula marcada
    jawCorner: { c: 142,   s: 3 },    // ángulo mandibular (grados): por debajo, refuerza la mandíbula marcada
    faceShort: { c: 1.165, s: 0.025 },// largo / ancho del rostro: por debajo, rostro corto (redondo)
    faceRound: { c: 0.955, s: 0.003 },// circularidad del contorno (4πA/P²): por encima, contorno redondeado
    lowFull:   { c: 0.907, s: 0.004 },// ancho del contorno al 30 % de la altura / ancho de pómulos: por encima, mejillas llenas
    jawSharp:  { c: 140,   s: 2.5 },  // ángulo mandibular (grados): por debajo, mandíbula angulosa
    eyeSmallW: { c: 0.185, s: 0.008 },// ancho del ojo / ancho del rostro: por debajo, ojo pequeño
    eyeSmallO: { c: 0.27,  s: 0.03 }, // apertura / ancho del ojo: por debajo, ojo pequeño
    eyeDroop:  { c: 1.5,   s: 1.5 },  // inclinación del ojo (grados): por debajo, ojo caído
    lipBig:    { c: 0.43,  s: 0.02 }, // alto de labios / ancho de boca: por encima, labios grandes
    lipThick:  { c: 0.141, s: 0.003 },// grosor de los dos labios (sin la apertura) / ancho del rostro: por encima, labios grandes
    lipMinThick: { c: 0.125, s: 0.004 },// por debajo de este grosor no pueden ser grandes (boca abierta o sonrisa)
    lipThin:   { c: 0.083, s: 0.01 }, // alto de labios / ancho del rostro: por debajo, labios finos
    lipAsymUD: { c: 1.75,  s: 0.08 }, // grosor labio inferior / superior: por encima, asimetría arriba-abajo
    lipAsymLR: { c: 0.15,  s: 0.04 }  // diferencia entre lado derecho e izquierdo: por encima, asimetría lateral
  };
  // Medidas que se muestran en el modo calibración
  var METRICS = ['aspect', 'circ', 'lowFull', 'jawR', 'jawAngle', 'lowJawR', 'foreR', 'eyeW', 'eyeOpen', 'tilt', 'browLid', 'depth', 'scleraLow', 'lipFull', 'lipFace', 'lipThick', 'lipUD', 'lipLR'];

  var THRESH = {
    traitMin: 1.0,       // puntuación mínima para mostrar un rasgo secundario de ojos/labios
    maxYaw: 16,          // grados de giro lateral permitidos
    maxPitch: 24,        // grados de inclinación arriba/abajo permitidos
    blink: 0.5,
    jawOpen: 0.25,
    smile: 0.75
  };

  /* ------------------------------------------------------------------ *
   * 2. CONTENIDO: se lee de saigu-visagismo-textos.js
   * ------------------------------------------------------------------ */
  var TX = window.SaiguVisagismoTextos || { areas: {}, categorias: {} };
  var CONTENT = {};
  Object.keys(TX.areas || {}).forEach(function (area) {
    var A = TX.areas[area], types = {};
    Object.keys(A.tipos || {}).forEach(function (k) {
      var T = A.tipos[k];
      types[k] = {
        name: T.nombre, short: T.resumen || '', intro: T.intro || '',
        steps: (T.pasos || []).map(function (p) {
          return { q: p.pregunta, t: p.texto, list: p.lista, p: p.categorias, handles: p.productos };
        })
      };
    });
    CONTENT[area] = { label: A.titulo, subtitle: A.subtitulo || '', types: types };
  });
  var CATEGORY_LABELS = TX.categorias || {};

  /* ------------------------------------------------------------------ *
   * 3. GEOMETRÍA Y CLASIFICACIÓN (funciones puras)
   * ------------------------------------------------------------------ */

  var IDX = {
    top: 10, chin: 152, cheekR: 234, cheekL: 454, foreR: 54, foreL: 284,
    jawR: 172, jawL: 397, lowJawR: 150, lowJawL: 379, ramusR: 93, ramusL: 323,
    chinSideR: 148, chinSideL: 377, nose: 1,
    oval: [10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379, 378, 400, 377,
           152, 148, 176, 149, 150, 136, 172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109],
    eyeR: { outer: 33, inner: 133, up: 159, low: 145, brow: [52, 65], browTop: [105, 66] },
    eyeL: { outer: 263, inner: 362, up: 386, low: 374, brow: [282, 295], browTop: [334, 296] },
    irisA: [468, 469, 470, 471, 472], irisB: [473, 474, 475, 476, 477],
    lip: { top: 0, upIn: 13, lowIn: 14, bottom: 17, cornerR: 61, cornerL: 291,
           peakR: 37, peakL: 267, lowR: 84, lowL: 314, upInR: 82, upInL: 312, lowInR: 87, lowInL: 317 }
  };

  function dist(a, b) { return Math.hypot(a.x - b.x, a.y - b.y); }
  function avg(pts) {
    var s = { x: 0, y: 0, z: 0 };
    pts.forEach(function (p) { s.x += p.x; s.y += p.y; s.z += p.z || 0; });
    return { x: s.x / pts.length, y: s.y / pts.length, z: s.z / pts.length };
  }
  function z(v, c) { return (v - c.c) / c.s; }
  function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }
  function angleAt(b, a, c) {
    var v1x = a.x - b.x, v1y = a.y - b.y, v2x = c.x - b.x, v2y = c.y - b.y;
    var cos = (v1x * v2x + v1y * v2y) / (Math.hypot(v1x, v1y) * Math.hypot(v2x, v2y));
    return Math.acos(clamp(cos, -1, 1)) * 180 / Math.PI;
  }

  /** Convierte landmarks normalizados a píxeles y corrige la inclinación (roll). */
  function normalize(lms, w, h) {
    var P = lms.map(function (p) { return { x: p.x * w, y: p.y * h, z: p.z * w }; });
    var r = avg([P[IDX.eyeR.outer], P[IDX.eyeR.inner]]);
    var l = avg([P[IDX.eyeL.outer], P[IDX.eyeL.inner]]);
    var ang = Math.atan2(l.y - r.y, l.x - r.x);
    if (l.x < r.x) ang = Math.atan2(r.y - l.y, r.x - l.x); // foto espejada
    var c = avg([r, l]);
    var cos = Math.cos(-ang), sin = Math.sin(-ang);
    var R = P.map(function (p) {
      var dx = p.x - c.x, dy = p.y - c.y;
      return { x: c.x + dx * cos - dy * sin, y: c.y + dx * sin + dy * cos, z: p.z };
    });
    return { P: R, roll: ang * 180 / Math.PI };
  }

  function poseFromMatrix(m) {
    if (!m || !m.data || m.data.length < 16) return null;
    var d = m.data; // column-major 4x4
    var r20 = d[2], r21 = d[6], r22 = d[10];
    var yaw = Math.asin(clamp(-r20, -1, 1)) * 180 / Math.PI;
    var pitch = Math.atan2(r21, r22) * 180 / Math.PI;
    return { yaw: yaw, pitch: pitch };
  }

  /**
   * Forma del contorno de la cara: circularidad (1 = círculo perfecto; un rostro
   * alargado o anguloso da menos) y ancho del contorno al 30 % de la altura desde
   * la barbilla (mejillas y tercio inferior llenos = rostro redondo).
   */
  function contour(P, W) {
    var pts = IDX.oval.map(function (k) { return P[k]; }), n = pts.length;
    var area = 0, per = 0, top = Infinity, bot = -Infinity;
    for (var i = 0; i < n; i++) {
      var a = pts[i], b = pts[(i + 1) % n];
      area += a.x * b.y - b.x * a.y;
      per += dist(a, b);
      top = Math.min(top, a.y); bot = Math.max(bot, a.y);
    }
    area = Math.abs(area) / 2;
    var y = bot - (bot - top) * 0.30, xs = [];
    for (var j = 0; j < n; j++) {
      var p = pts[j], q = pts[(j + 1) % n];
      if ((p.y - y) * (q.y - y) <= 0 && p.y !== q.y) xs.push(p.x + (y - p.y) * (q.x - p.x) / (q.y - p.y));
    }
    var low = xs.length >= 2 ? (Math.max.apply(null, xs) - Math.min.apply(null, xs)) / W : 0;
    return { circ: 4 * Math.PI * area / (per * per), lowFull: low };
  }

  function measure(P) {
    var W = dist(P[IDX.cheekR], P[IDX.cheekL]);
    var L = dist(P[IDX.top], P[IDX.chin]);
    var fore = dist(P[IDX.foreR], P[IDX.foreL]);
    var jaw = dist(P[IDX.jawR], P[IDX.jawL]);
    var lowJaw = dist(P[IDX.lowJawR], P[IDX.lowJawL]);
    var jawAngle = (angleAt(P[IDX.jawR], P[IDX.ramusR], P[IDX.chinSideR]) +
                    angleAt(P[IDX.jawL], P[IDX.ramusL], P[IDX.chinSideL])) / 2;

    function eye(E) {
      var o = P[E.outer], i = P[E.inner], u = P[E.up], lo = P[E.low];
      var ew = dist(o, i);
      var tilt = Math.atan2(i.y - o.y, Math.abs(o.x - i.x)) * 180 / Math.PI; // + = esquina externa más alta
      var brow = avg(E.brow.map(function (k) { return P[k]; }));
      var browTop = avg(E.browTop.map(function (k) { return P[k]; }));
      var lidZ = avg([u, lo]).z;
      return {
        w: ew, open: Math.abs(lo.y - u.y) / ew, tilt: tilt,
        browLid: Math.abs(u.y - brow.y) / ew,
        depth: (lidZ - avg([brow, browTop]).z) / W,
        center: avg([o, i]), low: lo, up: u
      };
    }
    var eR = eye(IDX.eyeR), eL = eye(IDX.eyeL);

    // Iris (si el modelo lo devuelve: 478 puntos)
    var scleraLow = 0, hasIris = P.length >= 478;
    if (hasIris) {
      [IDX.irisA, IDX.irisB].forEach(function (ids) {
        var c = P[ids[0]];
        var rad = ids.slice(1).reduce(function (s, k) { return s + dist(c, P[k]); }, 0) / 4;
        var e = dist(c, eR.center) < dist(c, eL.center) ? eR : eL;
        e.scleraLow = (e.low.y - (c.y + rad)) / (2 * rad);
      });
      scleraLow = ((eR.scleraLow || 0) + (eL.scleraLow || 0)) / 2;
    }

    var Lp = IDX.lip;
    var mouthW = dist(P[Lp.cornerR], P[Lp.cornerL]);
    var lipH = P[Lp.bottom].y - P[Lp.top].y;
    var upT = P[Lp.upIn].y - P[Lp.top].y;
    var lowT = P[Lp.bottom].y - P[Lp.lowIn].y;
    var sideR = (P[Lp.upInR].y - P[Lp.peakR].y) + (P[Lp.lowR].y - P[Lp.lowInR].y);
    var sideL = (P[Lp.upInL].y - P[Lp.peakL].y) + (P[Lp.lowL].y - P[Lp.lowInL].y);
    var cornerDiff = Math.abs(P[Lp.cornerR].y - P[Lp.cornerL].y) / mouthW;
    var lipLR = Math.max(Math.abs(sideR - sideL) / ((sideR + sideL) / 2 || 1), cornerDiff * 2);
    var shape = contour(P, W);

    return {
      aspect: L / W, circ: shape.circ, lowFull: shape.lowFull, jawR: jaw / W, lowJawR: lowJaw / W, foreR: fore / W, tri: jaw / fore,
      jawAngle: jawAngle,
      eyeW: (eR.w + eL.w) / 2 / W,
      eyeOpen: (eR.open + eL.open) / 2,
      tilt: (eR.tilt + eL.tilt) / 2,
      browLid: (eR.browLid + eL.browLid) / 2,
      depth: (eR.depth + eL.depth) / 2,
      scleraLow: scleraLow, hasIris: hasIris,
      lipFull: lipH / mouthW, lipFace: lipH / W, lipThick: (upT + lowT) / W,
      lipLR: lipLR, lipUD: upT > 0 ? lowT / upT : 0
    };
  }

  function rank(scores) {
    return Object.keys(scores).map(function (k) { return { type: k, score: +scores[k].toFixed(2) }; })
      .sort(function (a, b) { return b.score - a.score; });
  }

  function classify(m) {
    var R = RULES;
    // Puntuaciones por encima de 0 = rasgo presente
    // Mandíbula marcada: ancha respecto a los pómulos y con el ángulo poco abierto.
    var strong = z(m.jawR, R.jawStrong) - z(m.jawAngle, R.jawCorner);
    var sharp = -z(m.jawAngle, R.jawSharp);
    // Redondo: rostro corto, o bien contorno redondeado con el tercio inferior lleno.
    var round = Math.max(-z(m.aspect, R.faceShort),
                         Math.min(z(m.circ, R.faceRound), z(m.lowFull, R.lowFull)));

    // Mandíbula marcada: cuadrado (ángulo marcado) o triangular (ángulo suave).
    // Mandíbula suave: redondo u ovalado (el resto).
    var rostro = rank({
      cuadrado:   Math.min(strong, sharp),
      triangular: Math.min(strong, -sharp),
      ovalado:    Math.min(-strong, -round),
      redondo:    Math.min(-strong, round)
    });

    // En una foto frontal se puede medir el tamaño y la inclinación del ojo.
    // Encapotados, hundidos y prominentes dependen del volumen: se preguntan después.
    var ojos = rank({
      caidos:   -z(m.tilt, R.eyeDroop),
      pequenos: Math.min(-z(m.eyeW, R.eyeSmallW), -z(m.eyeOpen, R.eyeSmallO)),
      grandes:  0
    });

    // Labios asimétricos: de arriba abajo (inferior mucho más grueso que el superior)
    // o de lado a lado. Antes no se medía y solo salía «por descarte», así que un
    // labio inferior grueso inflaba el alto total y daba «grandes».
    // «Grandes»: labios altos respecto a la boca, o gruesos respecto a la cara (una boca
    // ancha con labios gruesos da un cociente alto/ancho normal). En los dos casos se
    // exige grosor real, para que una boca entreabierta o una sonrisa no cuente como volumen.
    var big = Math.min(Math.max(z(m.lipFull, R.lipBig), z(m.lipThick, R.lipThick)),
                       z(m.lipThick, R.lipMinThick));
    // Cuando el volumen es claro, que el inferior sea más grueso es lo normal y no
    // convierte los labios en asimétricos; la asimetría de lado a lado sí cuenta siempre.
    var asym = Math.max(z(m.lipLR, R.lipAsymLR), Math.min(z(m.lipUD, R.lipAsymUD), -big));
    // Si nada es claro, asimétricos sigue siendo la opción por defecto (puntuación 0).
    var labios = rank({
      grandes:     big,
      finos:       -z(m.lipFace, R.lipThin),
      asimetricos: Math.max(asym, 0)
    });

    return { rostro: rostro, ojos: pickTraits(ojos, ['grandes', 'pequenos']),
             labios: pickTraits(labios, ['grandes', 'finos']),
             z: { jawStrong: strong, faceRound: round, jawSharp: sharp, lipAsym: asym } };
  }

  /** Combina lo medido en la foto con las respuestas sobre el párpado y la profundidad del ojo. */
  function applyEyeAnswers(photoRanked, ans) {
    var scores = {};
    photoRanked.forEach(function (r) { scores[r.type] = r.score; });
    if (ans.parpado === 'poco') scores.encapotados = 3;
    if (ans.profundidad === 'hundidos') scores.hundidos = 2.5;
    if (ans.profundidad === 'prominentes') scores.prominentes = 2.5;
    return pickTraits(rank(scores), ['grandes', 'pequenos'], ['prominentes', 'hundidos']);
  }

  /** Quita rasgos incompatibles y marca cuáles superan el umbral. */
  function pickTraits(ranked) {
    var exclusive = Array.prototype.slice.call(arguments, 1);
    var seen = {};
    var out = [];
    ranked.forEach(function (r) {
      var blocked = exclusive.some(function (g) {
        return g.indexOf(r.type) > -1 && g.some(function (o) { return o !== r.type && seen[o]; });
      });
      if (!blocked) { out.push(r); seen[r.type] = true; }
    });
    return out;
  }

  function qualityIssues(m, blend, pose, faceBox) {
    var issues = [];
    if (pose) {
      if (Math.abs(pose.yaw) > THRESH.maxYaw) issues.push('Tienes la cara un poco girada. Mira de frente a la cámara.');
      if (Math.abs(pose.pitch) > THRESH.maxPitch) issues.push('Tienes la cabeza inclinada hacia arriba o hacia abajo. Mantén la barbilla recta.');
    }
    if (blend) {
      if ((blend.eyeBlinkLeft || 0) > THRESH.blink || (blend.eyeBlinkRight || 0) > THRESH.blink) issues.push('Parece que tienes los ojos cerrados o entornados.');
      if ((blend.jawOpen || 0) > THRESH.jawOpen) issues.push('Tienes la boca abierta. Ciérrala de forma relajada.');
      if ((blend.mouthSmileLeft || 0) > THRESH.smile || (blend.mouthSmileRight || 0) > THRESH.smile) issues.push('Estás sonriendo. Para analizar bien los labios, pon una expresión neutra.');
    }
    if (faceBox && faceBox < 0.18) issues.push('Tu cara sale muy pequeña en la foto. Acércate un poco más.');
    return issues;
  }

  /* ------------------------------------------------------------------ *
   * 4. MODELO
   * ------------------------------------------------------------------ */
  var landmarkerPromise = null;
  var cpuPromise = null;
  var filesetPromise = null;

  /*
   * Carga un módulo ES desde una URL.
   * Shopify minifica los .js del tema y convierte «import()» en «require()», que
   * no existe en el navegador. Por eso la llamada va dentro de un texto que el
   * minificador no toca. Si el navegador no permite «new Function», se usa un
   * <script type="module"> como alternativa.
   */
  function importModule(url) {
    var dyn = null;
    try { dyn = new Function('u', 'return imp' + 'ort(u)'); } catch (e) { dyn = null; }
    if (dyn) {
      try { return Promise.resolve(dyn(url)).catch(function () { return importViaScript(url); }); }
      catch (e) { return importViaScript(url); }
    }
    return importViaScript(url);
  }
  function importViaScript(url) {
    return new Promise(function (resolve, reject) {
      var key = '__svImport' + Math.random().toString(36).slice(2);
      window[key] = {
        ok: function (m) { try { delete window[key]; } catch (e) {} resolve(m); },
        ko: function (err) { try { delete window[key]; } catch (e) {} reject(err); }
      };
      var s = document.createElement('script');
      s.type = 'module';
      s.textContent = 'imp' + 'ort(' + JSON.stringify(url) + ').then(function (m) { window["' + key + '"].ok(m); }, function (e) { window["' + key + '"].ko(e); });';
      document.head.appendChild(s);
    });
  }

  function loadFileset(cfg) {
    if (!filesetPromise) {
      filesetPromise = importModule(cfg.moduleUrl).then(function (vision) {
        return vision.FilesetResolver.forVisionTasks(cfg.wasmBase).then(function (fileset) {
          return { vision: vision, fileset: fileset };
        });
      });
      filesetPromise.catch(function () { filesetPromise = null; });
    }
    return filesetPromise;
  }

  function createLandmarker(cfg, delegate) {
    return loadFileset(cfg).then(function (f) {
      return f.vision.FaceLandmarker.createFromOptions(f.fileset, {
        baseOptions: { modelAssetPath: cfg.modelUrl, delegate: delegate },
        runningMode: 'IMAGE', numFaces: 2,
        outputFaceBlendshapes: true, outputFacialTransformationMatrixes: true
      });
    });
  }

  function loadLandmarker(cfg) {
    if (landmarkerPromise) return landmarkerPromise;
    landmarkerPromise = createLandmarker(cfg, 'GPU').catch(function () { return loadCpuLandmarker(cfg); });
    landmarkerPromise.catch(function () { landmarkerPromise = null; });
    return landmarkerPromise;
  }

  // Algunos equipos crean el modelo con GPU pero fallan al analizar: reintentamos con CPU
  function loadCpuLandmarker(cfg) {
    if (cpuPromise) return cpuPromise;
    cpuPromise = createLandmarker(cfg, 'CPU');
    cpuPromise.catch(function () { cpuPromise = null; });
    return cpuPromise;
  }

  function analyzeCanvas(landmarker, canvas) {
    var res = landmarker.detect(canvas);
    if (!res || !res.faceLandmarks || !res.faceLandmarks.length) return { error: 'noface' };
    // Si hay varias caras, nos quedamos con la más grande
    var best = 0, bestArea = 0;
    res.faceLandmarks.forEach(function (lm, i) {
      var xs = lm.map(function (p) { return p.x; }), ys = lm.map(function (p) { return p.y; });
      var a = (Math.max.apply(null, xs) - Math.min.apply(null, xs)) * (Math.max.apply(null, ys) - Math.min.apply(null, ys));
      if (a > bestArea) { bestArea = a; best = i; }
    });
    var raw = res.faceLandmarks[best];
    var n = normalize(raw, canvas.width, canvas.height);
    var m = measure(n.P);
    var blend = null;
    if (res.faceBlendshapes && res.faceBlendshapes[best]) {
      blend = {};
      res.faceBlendshapes[best].categories.forEach(function (c) { blend[c.categoryName] = c.score; });
    }
    var pose = poseFromMatrix(res.facialTransformationMatrixes && res.facialTransformationMatrixes[best]);
    var faceW = Math.max.apply(null, raw.map(function (p) { return p.x; })) - Math.min.apply(null, raw.map(function (p) { return p.x; }));
    var c = classify(m);
    return {
      raw: raw, metrics: m, pose: pose, roll: n.roll, blend: blend, multiple: res.faceLandmarks.length > 1,
      issues: qualityIssues(m, blend, pose, faceW), result: c
    };
  }

  /* ------------------------------------------------------------------ *
   * 5. INTERFAZ
   * ------------------------------------------------------------------ */
  function h(tag, attrs) {
    var el = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) {
      var v = attrs[k];
      if (v == null || v === false) return;
      if (k === 'class') el.className = v;
      else if (k === 'text') el.textContent = v;
      else if (k === 'html') el.innerHTML = v;
      else if (k.slice(0, 2) === 'on') el.addEventListener(k.slice(2), v);
      else el.setAttribute(k, v === true ? '' : v);
    });
    for (var i = 2; i < arguments.length; i++) {
      var c = arguments[i];
      if (c == null || c === false) continue;
      if (Array.isArray(c)) c.forEach(function (x) { if (x) el.appendChild(typeof x === 'string' ? document.createTextNode(x) : x); });
      else el.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    }
    return el;
  }

  // Puntos de contorno que dibujamos sobre la foto durante el análisis
  var OVERLAY = {
    face: [10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379, 378, 400, 377, 152, 148, 176, 149, 150, 136, 172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109],
    eyeR: [33, 246, 161, 160, 159, 158, 157, 173, 133, 155, 154, 153, 145, 144, 163, 7],
    eyeL: [263, 466, 388, 387, 386, 385, 384, 398, 362, 382, 381, 380, 374, 373, 390, 249],
    lips: [61, 185, 40, 39, 37, 0, 267, 269, 270, 409, 291, 375, 321, 405, 314, 17, 84, 181, 91, 146],
    browR: [70, 63, 105, 66, 107], browL: [300, 293, 334, 296, 336]
  };

  function App(root) {
    this.root = root;
    var cfgEl = root.querySelector('script[data-sv-config]');
    var prodEl = root.querySelector('script[data-sv-products]');
    this.cfg = Object.assign({}, DEFAULTS, safeJSON(cfgEl));
    if (!this.cfg.modelUrl) this.cfg.modelUrl = DEFAULTS.modelUrl;
    this.products = safeJSON(prodEl) || {};
    this.images = this.cfg.images || {};
    this.debug = /[?&]visagismo_debug=1/.test(location.search) || this.cfg.debug === true;
    this.state = { screen: 'intro', photo: null, analysis: null, choice: {}, tab: 'rostro' };
    // Enlace directo a un resultado (el que se envía por email): ?visagismo=ovalado.caidos.finos
    var linked = (location.search.match(/[?&]visagismo=([a-z]+)\.([a-z]+)\.([a-z]+)/) || []);
    if (linked.length && CONTENT.rostro.types[linked[1]] && CONTENT.ojos.types[linked[2]] && CONTENT.labios.types[linked[3]]) {
      this.state.choice = { rostro: linked[1], ojos: linked[2], labios: linked[3] };
      this.state.screen = 'results';
      this.state.emailDone = true;
      this.state.fromLink = true;
    }
    this.stage = h('div', { class: 'sv-stage', 'aria-live': 'polite' });
    root.appendChild(this.stage);
    this.toast = h('div', { class: 'sv-toast', role: 'status', hidden: true });
    root.appendChild(this.toast);
    this.fileInput = h('input', { type: 'file', accept: 'image/*', class: 'sv-visually-hidden', id: 'sv-file-' + Math.random().toString(36).slice(2, 7), tabindex: '-1' });
    this.fileInput.addEventListener('change', this.onFile.bind(this));
    root.appendChild(this.fileInput);
    this.render();
    this.emit(this.state.fromLink ? 'visagismo_resultado_abierto' : 'visagismo_vista', this.state.fromLink ? { rostro: this.state.choice.rostro, ojos: this.state.choice.ojos, labios: this.state.choice.labios } : {});
  }

  function safeJSON(el) { try { return el ? JSON.parse(el.textContent) : null; } catch (e) { return null; } }

  App.prototype.go = function (screen) {
    this.stopCamera();
    this.state.screen = screen;
    this.render();
    var top = this.root.getBoundingClientRect().top;
    if (top < 0) this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  App.prototype.render = function () {
    var s = this.state.screen;
    this.stage.innerHTML = '';
    this.root.setAttribute('data-screen', s);
    var view = this['view_' + s]();
    this.stage.appendChild(view);
    var focusEl = view.querySelector('[data-autofocus]') || view.querySelector('h2, h3');
    if (focusEl && this._rendered) { focusEl.setAttribute('tabindex', '-1'); focusEl.focus({ preventScroll: true }); }
    this._rendered = true;
  };

  /* ---------- Consejos para la foto (desplegable) ---------- */
  var TIPS = [
    ['Mira de frente', 'con la cara recta y la cámara a la altura de los ojos'],
    ['Luz natural de frente', 'junto a una ventana, sin sombras fuertes'],
    ['Pelo recogido', 'con la frente y la línea de la mandíbula despejadas'],
    ['Sin gafas', 'y con la cara lavada o muy poco maquillaje'],
    ['Expresión neutra', 'boca cerrada y sin sonreír']
  ];
  App.prototype.tipsBox = function () {
    var self = this;
    var listId = 'sv-tips-list';
    var label = h('span');
    var list = h('ul', { class: 'sv-tips__list', id: listId },
      TIPS.map(function (t) { return h('li', null, iconCheck(), h('span', null, h('strong', { text: t[0] }), ': ' + t[1])); }));
    var btn = h('button', { class: 'sv-tips__toggle', type: 'button', 'aria-controls': listId, onclick: function () {
      self.state.tipsOpen = !self.state.tipsOpen; sync();
    } },
      iconBulb(),
      h('span', { class: 'sv-tips__title', text: '5 consejos para que la foto salga bien' }),
      h('span', { class: 'sv-tips__more' }, label, iconChevron())
    );
    var box = h('div', { class: 'sv-tips' }, btn, list);
    function sync() {
      var open = !!self.state.tipsOpen;
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      list.hidden = !open;
      label.textContent = open ? 'Ocultar' : 'Ver';
      box.classList.toggle('is-open', open);
    }
    sync();
    return box;
  };

  /* ---------- Intro ---------- */
  App.prototype.view_intro = function () {
    var self = this;
    var c = this.cfg;
    var imgs = this.images;
    var media;
    if (c.introImage) {
      media = h('img', { class: 'sv-intro__img', src: c.introImage, alt: '' });
    } else {
      // Sin imagen propia: mosaico con las ilustraciones de los tipos de rostro
      var faces = ['ovalado', 'cuadrado', 'triangular', 'redondo'].filter(function (k) { return imgs['rostro_' + k]; });
      media = faces.length
        ? h('div', { class: 'sv-intro__grid' }, faces.map(function (k) {
            return h('figure', null, h('img', { src: imgs['rostro_' + k], alt: '' }), h('figcaption', { text: CONTENT.rostro.types[k].name }));
          }))
        : null;
    }
    return h('div', { class: 'sv-intro' + (media ? '' : ' sv-intro--solo') },
      media ? h('div', { class: 'sv-intro__media', 'aria-hidden': 'true' }, h('span', { class: 'sv-cross sv-cross--tl' }), h('span', { class: 'sv-cross sv-cross--tr' }), media, h('span', { class: 'sv-cross sv-cross--bl' }), h('span', { class: 'sv-cross sv-cross--br' })) : null,
      h('div', { class: 'sv-intro__copy' },
        h('h2', { class: 'sv-title', text: c.heading || 'Descubre cómo maquillar tu rostro, tus ojos y tus labios' }),
        h('p', { class: 'sv-lead', text: c.subheading || 'Hazte una foto o sube una imagen. Analizamos la forma de tu rostro, tus ojos y tus labios y te enseñamos dónde aplicar cada producto para realzar tus rasgos.' }),
        h('div', { class: 'sv-actions sv-actions--center' },
          h('button', { class: 'sv-btn sv-btn--primary', type: 'button', onclick: function () { self.startCamera(); } }, iconCamera(), 'Hacerme una foto'),
          h('button', { class: 'sv-btn sv-btn--ghost', type: 'button', onclick: function () { self.pickFile(); } }, iconUpload(), 'Subir una imagen')
        ),
        h('p', { class: 'sv-privacy' }, iconLock(), h('span', { text: c.privacy || 'Tu foto se analiza en tu propio dispositivo. No la guardamos ni la enviamos a ningún servidor.' })),
        this.tipsBox(),
        h('button', { class: 'sv-btn sv-btn--ghost sv-btn--manual', type: 'button', onclick: function () { self.started('manual'); self.go('manual'); } }, 'Prefiero elegir mis rasgos yo misma')
      )
    );
  };

  /* ---------- Cámara ---------- */
  App.prototype.startCamera = function () {
    var self = this;
    this.started('camara');
    this.warmModel();
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.showToast('Tu navegador no permite usar la cámara aquí. Sube una imagen.');
      this.pickFile();
      return;
    }
    this.state.screen = 'camera';
    this.render();
    navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 1280 } }, audio: false })
      .then(function (stream) {
        if (self.state.screen !== 'camera') { stream.getTracks().forEach(function (t) { t.stop(); }); return; }
        self.stream = stream;
        self.video.srcObject = stream;
        return self.video.play();
      })
      .catch(function (err) {
        var name = err && err.name;
        if (name === 'NotAllowedError' || name === 'SecurityError') {
          // Dentro del editor del tema (iframe) la cámara nunca está disponible
          var inFrame = false;
          try { inFrame = window.self !== window.top; } catch (e) { inFrame = true; }
          self.state.issue = { camera: inFrame ? 'frame' : 'denied' };
          self.go('issue');
        } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
          self.go('intro');
          self.showToast('No encontramos ninguna cámara en este dispositivo. Sube una imagen.');
        } else if (name === 'NotReadableError') {
          self.go('intro');
          self.showToast('La cámara está ocupada por otra aplicación. Ciérrala y vuelve a intentarlo, o sube una imagen.');
        } else {
          self.go('intro');
          self.showToast('No hemos podido abrir la cámara. Prueba a subir una imagen.');
        }
      });
  };

  App.prototype.stopCamera = function () {
    if (this.stream) { this.stream.getTracks().forEach(function (t) { t.stop(); }); this.stream = null; }
    if (this.countdownTimer) { clearInterval(this.countdownTimer); this.countdownTimer = null; }
  };

  App.prototype.view_camera = function () {
    var self = this;
    this.video = h('video', { class: 'sv-video', playsinline: true, muted: true, autoplay: true });
    this.video.muted = true;
    var count = h('div', { class: 'sv-count', hidden: true });
    var shoot = h('button', { class: 'sv-btn sv-btn--primary sv-shoot', type: 'button', 'data-autofocus': true, onclick: function () {
      if (!self.video.videoWidth) return;
      shoot.disabled = true;
      var n = 3;
      count.hidden = false; count.textContent = n;
      self.countdownTimer = setInterval(function () {
        n--;
        if (n > 0) { count.textContent = n; return; }
        clearInterval(self.countdownTimer); self.countdownTimer = null;
        self.capture();
      }, 800);
    } }, iconCamera(), 'Hacer la foto');
    return h('div', { class: 'sv-camera' },
      h('div', { class: 'sv-viewfinder' },
        this.video,
        h('div', { class: 'sv-guide', 'aria-hidden': 'true' }),
        count,
        h('p', { class: 'sv-viewfinder__hint', text: 'Encaja tu cara en el óvalo y mira a la cámara' })
      ),
      h('div', { class: 'sv-actions sv-actions--center' },
        shoot,
        h('button', { class: 'sv-btn sv-btn--ghost', type: 'button', onclick: function () { self.go('intro'); } }, 'Cancelar')
      )
    );
  };

  App.prototype.capture = function () {
    var v = this.video;
    var canvas = document.createElement('canvas');
    var scale = Math.min(1, 1280 / Math.max(v.videoWidth, v.videoHeight));
    canvas.width = Math.round(v.videoWidth * scale);
    canvas.height = Math.round(v.videoHeight * scale);
    var ctx = canvas.getContext('2d');
    // Guardamos la foto como se ve en el espejo, que es como la clienta se reconoce
    ctx.translate(canvas.width, 0); ctx.scale(-1, 1);
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    this.stopCamera();
    this.runAnalysis(canvas);
  };

  /* ---------- Subida ---------- */
  App.prototype.pickFile = function () { this.started('subida'); this.warmModel(); this.fileInput.value = ''; this.fileInput.click(); };

  App.prototype.onFile = function () {
    var self = this;
    var file = this.fileInput.files && this.fileInput.files[0];
    if (!file) return;
    if (!/^image\//.test(file.type) && !/\.(jpe?g|png|webp|heic|heif)$/i.test(file.name)) {
      this.showToast('Ese archivo no es una imagen. Sube una foto en JPG o PNG.');
      return;
    }
    var url = URL.createObjectURL(file);
    var img = new Image();
    img.onload = function () {
      var scale = Math.min(1, 1600 / Math.max(img.naturalWidth, img.naturalHeight));
      var canvas = document.createElement('canvas');
      canvas.width = Math.round(img.naturalWidth * scale);
      canvas.height = Math.round(img.naturalHeight * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      self.runAnalysis(canvas);
    };
    img.onerror = function () {
      URL.revokeObjectURL(url);
      self.showToast('No hemos podido abrir esa imagen. Prueba con una foto en JPG o PNG.');
    };
    img.src = url;
  };

  /* ---------- Análisis ---------- */
  App.prototype.warmModel = function () { loadLandmarker(this.cfg).catch(function () {}); };

  App.prototype.runAnalysis = function (canvas) {
    var self = this;
    this.state.photo = canvas;
    this.state.photoUrl = canvas.toDataURL('image/jpeg', 0.85);
    this.state.screen = 'analyzing';
    this.render();
    var started = Date.now();
    var cfg = this.cfg;
    loadLandmarker(cfg).catch(function (err) {
      var e = err instanceof Error ? err : new Error(String((err && (err.message || err.type)) || err));
      e.stage = 'load';
      throw e;
    }).then(function (lm) {
      try { return analyzeCanvas(lm, canvas); }
      catch (e) {
        if (window.console) console.warn('[Visagismo] Reintentando el análisis sin GPU', e);
        return loadCpuLandmarker(cfg).then(function (cpu) { return analyzeCanvas(cpu, canvas); });
      }
    }).then(function (a) {
      // Dejamos que la animación se vea al menos un momento
      var wait = Math.max(0, 1600 - (Date.now() - started));
      if (!a.error) self.drawOverlay(a.raw);
      setTimeout(function () {
        if (a.error === 'noface') { self.state.issue = { noface: true }; self.go('issue'); return; }
        self.state.analysis = a;
        self.emit('visagismo_foto_analizada', { avisos: a.issues.length });
        a.photoOjos = a.result.ojos;
        self.state.eyeAnswers = null;
        self.state.eyeStep = 1;
        self.state.eyeCropUrl = undefined;
        self.state.choice = {
          rostro: a.result.rostro[0].type,
          ojos: a.result.ojos[0].type,
          labios: a.result.labios[0].type
        };
        if (a.issues.length) { self.state.issue = { list: a.issues }; self.go('issue'); }
        else self.go('eyes');
      }, wait);
    }).catch(function (err) {
      if (window.console) console.error('[Visagismo] Error en el análisis', err);
      var detail = (err && (err.message || err.name)) || String(err);
      if (err && err.stage === 'load') self.state.issue = { model: true, detail: detail };
      else self.state.issue = { failed: true, detail: detail };
      self.go('issue');
    });
  };

  App.prototype.view_analyzing = function () {
    this.overlay = h('canvas', { class: 'sv-overlay', 'aria-hidden': 'true' });
    return h('div', { class: 'sv-analyzing' },
      h('div', { class: 'sv-photo sv-photo--scan' },
        h('img', { src: this.state.photoUrl, alt: 'Tu foto' }),
        this.overlay,
        h('div', { class: 'sv-scanline', 'aria-hidden': 'true' })
      ),
      h('div', { class: 'sv-analyzing__text' },
        h('h2', { class: 'sv-title sv-title--sm', text: 'Analizando tus rasgos…' }),
        h('ol', { class: 'sv-checklist' },
          h('li', { text: 'Proporciones del rostro' }),
          h('li', { text: 'Forma, tamaño e inclinación de los ojos' }),
          h('li', { text: 'Volumen y simetría de los labios' })
        ),
        h('p', { class: 'sv-muted', text: 'La primera vez puede tardar unos segundos mientras se prepara el análisis.' })
      )
    );
  };

  App.prototype.drawOverlay = function (raw) {
    var cv = this.overlay, photo = this.state.photo;
    if (!cv || !photo) return;
    cv.width = photo.width; cv.height = photo.height;
    var ctx = cv.getContext('2d');
    var color = getComputedStyle(this.root).getPropertyValue('--sv-mark').trim() || '#f3e6df';
    var r = Math.max(1.5, photo.width / 420);
    ctx.lineWidth = r * 0.7;
    ctx.strokeStyle = color; ctx.fillStyle = color;
    Object.keys(OVERLAY).forEach(function (k) {
      var ids = OVERLAY[k];
      ctx.globalAlpha = 0.55;
      ctx.beginPath();
      ids.forEach(function (id, i) {
        var p = raw[id]; var x = p.x * cv.width, y = p.y * cv.height;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      });
      if (k !== 'browR' && k !== 'browL') ctx.closePath();
      ctx.stroke();
      ctx.globalAlpha = 0.95;
      ids.forEach(function (id) {
        var p = raw[id];
        ctx.beginPath(); ctx.arc(p.x * cv.width, p.y * cv.height, r, 0, Math.PI * 2); ctx.fill();
      });
    });
    cv.classList.add('is-on');
  };

  /* ---------- Problemas con la foto ---------- */
  App.prototype.view_issue = function () {
    var self = this, is = this.state.issue || {};
    var title, body, allowContinue = false;
    if (is.camera) {
      return this.cameraHelp(is.camera);
    }
    if (is.model) {
      title = 'No hemos podido preparar el análisis';
      body = [h('p', { text: 'Revisa tu conexión e inténtalo de nuevo. Mientras tanto, puedes elegir tus rasgos manualmente.' })];
    } else if (is.failed) {
      title = 'No hemos podido analizar esta imagen';
      body = [h('p', { text: 'Prueba con otra foto o hazte una con la cámara. También puedes elegir tus rasgos manualmente.' })];
    } else if (is.noface) {
      title = 'No encontramos una cara en la foto';
      body = [h('p', { text: 'Asegúrate de que tu cara se ve completa, de frente y con buena luz.' })];
    } else {
      title = 'Tu foto se puede mejorar';
      allowContinue = true;
      body = [h('ul', { class: 'sv-issues' }, (is.list || []).map(function (t) { return h('li', { text: t }); })),
              h('p', { class: 'sv-muted', text: 'Con una foto mejor el resultado será más fiable, pero puedes ver el análisis de todas formas.' })];
    }
    return h('div', { class: 'sv-issue' },
      this.state.photoUrl && !is.model ? h('div', { class: 'sv-photo sv-photo--sm' }, h('img', { src: this.state.photoUrl, alt: 'Tu foto' })) : null,
      h('div', null,
        h('h2', { class: 'sv-title sv-title--sm', text: title }),
        body,
        h('div', { class: 'sv-actions' },
          h('button', { class: 'sv-btn sv-btn--primary', type: 'button', 'data-autofocus': true, onclick: function () { self.startCamera(); } }, iconCamera(), 'Repetir la foto'),
          h('button', { class: 'sv-btn sv-btn--ghost', type: 'button', onclick: function () { self.pickFile(); } }, iconUpload(), 'Subir otra imagen'),
          allowContinue ? h('button', { class: 'sv-btn sv-btn--ghost', type: 'button', onclick: function () { self.go('eyes'); } }, 'Continuar con esta foto') : null,
          (is.model || is.failed) ? h('button', { class: 'sv-btn sv-btn--ghost', type: 'button', onclick: function () { self.go('manual'); } }, 'Elegir mis rasgos') : null
        ),
        is.detail ? h('p', { class: 'sv-errcode', text: 'Detalle técnico: ' + is.detail }) : null
      )
    );
  };

  /* ---------- Cámara bloqueada: instrucciones según el dispositivo ---------- */
  App.prototype.cameraHelp = function (kind) {
    var self = this;
    var ua = navigator.userAgent || '';
    var ios = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && 'ontouchend' in document);
    var android = /Android/.test(ua);
    var safariMac = !ios && /Safari/.test(ua) && !/Chrome|Chromium|Edg|OPR/.test(ua);
    var firefox = /Firefox/.test(ua);
    var steps;
    if (kind === 'frame') {
      steps = ['Estás viendo el test dentro del editor de la tienda, donde el navegador no deja usar la cámara.', 'Abre la página del test directamente en una pestaña nueva y vuelve a intentarlo.'];
    } else if (ios && /CriOS/.test(ua)) {
      steps = ['Abre los Ajustes del iPhone.', 'Busca Chrome y activa «Cámara».', 'Vuelve aquí y pulsa «Volver a intentarlo».'];
    } else if (ios) {
      steps = ['Pulsa el icono «aA» (o el de ajustes) junto a la dirección de la web.', 'Entra en «Ajustes del sitio web» y en «Cámara» elige «Permitir».', 'Si no aparece, ve a Ajustes del iPhone > Safari > Cámara y elige «Preguntar» o «Permitir».', 'Recarga la página y pulsa «Volver a intentarlo».'];
    } else if (android) {
      steps = ['Pulsa el icono a la izquierda de la dirección de la web.', 'Entra en «Permisos» y activa «Cámara».', 'Recarga la página y pulsa «Volver a intentarlo».'];
    } else if (safariMac) {
      steps = ['En el menú Safari, abre «Ajustes» > «Sitios web» > «Cámara».', 'Busca esta web y elige «Permitir».', 'Recarga la página y pulsa «Volver a intentarlo».'];
    } else if (firefox) {
      steps = ['Pulsa el icono de la cámara tachada junto a la dirección de la web.', 'Quita el bloqueo de «Usar la cámara».', 'Recarga la página y pulsa «Volver a intentarlo».'];
    } else {
      steps = ['Pulsa el icono de ajustes (o el candado) a la izquierda de la dirección de la web.', 'En «Cámara», elige «Permitir».', 'Recarga la página y pulsa «Volver a intentarlo».'];
    }
    return h('div', { class: 'sv-issue sv-camhelp' },
      h('div', null,
        h('h2', { class: 'sv-title sv-title--sm', 'data-autofocus': true, text: kind === 'frame' ? 'La cámara no está disponible aquí' : 'Tu navegador tiene bloqueada la cámara para esta web' }),
        h('p', { text: kind === 'frame' ? 'Puedes hacer el test subiendo una foto o abrirlo en una pestaña nueva.' : 'Para hacerte la foto, permite el acceso a la cámara. Solo se usa para el análisis y la foto no sale de tu dispositivo.' }),
        h('ol', { class: 'sv-camhelp__steps' }, steps.map(function (t) { return h('li', { text: t }); })),
        h('div', { class: 'sv-actions' },
          h('button', { class: 'sv-btn sv-btn--primary', type: 'button', onclick: function () { self.pickFile(); } }, iconUpload(), 'Subir una imagen'),
          kind === 'frame'
            ? h('a', { class: 'sv-btn sv-btn--ghost', href: location.href, target: '_blank', rel: 'noopener' }, 'Abrir en una pestaña nueva')
            : h('button', { class: 'sv-btn sv-btn--ghost', type: 'button', onclick: function () { self.startCamera(); } }, iconCamera(), 'Volver a intentarlo')
        )
      )
    );
  };

  /* ---------- Preguntas sobre los ojos ---------- */
  // El párpado encapotado y la profundidad del ojo no se aprecian bien en una
  // foto de frente, así que se lo preguntamos a la clienta mostrándole sus ojos.
  App.prototype.eyeCrop = function () {
    var a = this.state.analysis, photo = this.state.photo;
    if (!a || !a.raw || !photo) return null;
    var ids = OVERLAY.eyeR.concat(OVERLAY.eyeL, OVERLAY.browR, OVERLAY.browL);
    var xs = ids.map(function (k) { return a.raw[k].x * photo.width; });
    var ys = ids.map(function (k) { return a.raw[k].y * photo.height; });
    var x0 = Math.min.apply(null, xs), x1 = Math.max.apply(null, xs);
    var y0 = Math.min.apply(null, ys), y1 = Math.max.apply(null, ys);
    var padX = (x1 - x0) * 0.12, padY = (y1 - y0) * 0.35;
    x0 = Math.max(0, x0 - padX); x1 = Math.min(photo.width, x1 + padX);
    y0 = Math.max(0, y0 - padY); y1 = Math.min(photo.height, y1 + padY);
    var c = document.createElement('canvas');
    var scale = Math.min(2, 900 / (x1 - x0));
    c.width = Math.round((x1 - x0) * scale); c.height = Math.round((y1 - y0) * scale);
    c.getContext('2d').drawImage(photo, x0, y0, x1 - x0, y1 - y0, 0, 0, c.width, c.height);
    try { return c.toDataURL('image/jpeg', 0.88); } catch (e) { return null; }
  };

  // Una pregunta por pantalla: al elegir una respuesta se pasa a la siguiente.
  App.prototype.view_eyes = function () {
    var self = this;
    var ans = this.state.eyeAnswers = this.state.eyeAnswers || {};
    var step = this.state.eyeStep || 1;
    if (this.state.eyeCropUrl === undefined) this.state.eyeCropUrl = this.eyeCrop();
    var crop = this.state.eyeCropUrl;
    var imgs = this.images;

    var Q = step === 1 ? {
      key: 'parpado',
      title: 'Con los ojos abiertos y mirando al frente, ¿se ve tu párpado móvil?',
      hint: null,
      options: [
        { v: 'bien', label: 'Sí, se ve bien', desc: 'Hay espacio visible entre las pestañas y el pliegue del párpado.', img: imgs.q_parpado_bien || imgs.ojos_grandes },
        { v: 'poco', label: 'Se ve poco o nada', desc: 'El párpado fijo cae y tapa casi todo el párpado móvil.', img: imgs.q_parpado_poco || imgs.ojos_encapotados }
      ]
    } : {
      key: 'profundidad',
      title: '¿Cómo son tus ojos respecto al hueso de la ceja?',
      hint: 'Si dudas, mírate de perfil en el espejo.',
      options: [
        { v: 'hundidos', label: 'Hundidos', desc: 'El hueso de la ceja hace sombra sobre el ojo.', img: imgs.q_prof_hundidos || imgs.ojos_hundidos },
        { v: 'prominentes', label: 'Prominentes', desc: 'El ojo sobresale un poco hacia fuera.', img: imgs.q_prof_prominentes || imgs.ojos_prominentes },
        { v: 'normal', label: 'Ni una cosa ni otra', desc: 'No noto que estén ni hundidos ni salidos.', img: imgs.q_prof_normal }
      ]
    };

    var busy = false;
    function choose(v, btn) {
      if (busy) return;
      busy = true;
      ans[Q.key] = v;
      btn.setAttribute('aria-pressed', 'true');
      btn.classList.add('is-selected');
      // Pequeña pausa para que se vea la opción marcada
      setTimeout(function () {
        if (step === 1) { self.state.eyeStep = 2; self.render(); return; }
        var a = self.state.analysis;
        a.result.ojos = applyEyeAnswers(a.photoOjos, ans);
        self.state.choice.ojos = a.result.ojos[0].type;
        self.finish();
      }, 220);
    }

    var options = h('div', { class: 'sv-options', role: 'group', 'aria-labelledby': 'sv-q-title' }, Q.options.map(function (o) {
      var btn = h('button', { type: 'button', class: 'sv-option' + (ans[Q.key] === o.v ? ' is-selected' : ''), 'aria-pressed': ans[Q.key] === o.v ? 'true' : 'false' },
        o.img ? h('img', { class: 'sv-ratio--ojos', src: o.img, alt: '', loading: 'lazy' }) : h('span', { class: 'sv-option__ph sv-ratio--ojos', 'aria-hidden': 'true' }),
        h('span', { class: 'sv-option__name', text: o.label }),
        o.desc ? h('span', { class: 'sv-option__desc', text: o.desc }) : null);
      btn.addEventListener('click', function () { choose(o.v, btn); });
      return btn;
    }));

    return h('div', { class: 'sv-manual sv-eyesq' },
      h('p', { class: 'sv-eyebrow sv-step-count', text: 'Pregunta ' + step + ' de 2 · Tus ojos' }),
      h('h2', { class: 'sv-title sv-title--sm', id: 'sv-q-title', 'data-autofocus': true, text: Q.title }),
      Q.hint ? h('p', { class: 'sv-muted sv-q-hint', text: Q.hint }) : null,
      step === 1 ? h('p', { class: 'sv-muted sv-q-hint', text: 'El párpado móvil es la piel que se mueve al parpadear, entre las pestañas y el pliegue. Mírate al espejo: ¿se ve con el ojo abierto?' }) : null,
      crop ? h('div', { class: 'sv-eyecrop' }, h('img', { src: crop, alt: 'Tus ojos en la foto' })) : null,
      options,
      step === 2 ? h('div', { class: 'sv-actions sv-actions--center' },
        h('button', { class: 'sv-btn sv-btn--ghost sv-btn--back', type: 'button', onclick: function () { self.state.eyeStep = 1; self.render(); } }, iconBack(), 'Volver a la pregunta anterior')) : null
    );
  };

  /* ---------- Selección manual ---------- */
  App.prototype.view_manual = function () {
    var self = this;
    var choice = Object.assign({ rostro: 'ovalado', ojos: 'grandes', labios: 'finos' }, this.state.choice);
    var groups = ['rostro', 'ojos', 'labios'].map(function (area) {
      var types = CONTENT[area].types;
      return h('fieldset', { class: 'sv-fieldset' },
        h('legend', { text: CONTENT[area].label }),
        h('div', { class: 'sv-options' }, Object.keys(types).map(function (k) {
          var id = 'sv-m-' + area + '-' + k;
          var input = h('input', { type: 'radio', name: 'sv-m-' + area, id: id, value: k, checked: choice[area] === k });
          input.addEventListener('change', function () { choice[area] = k; });
          return h('label', { class: 'sv-option', for: id }, input,
            self.images[area + '_' + k] ? h('img', { class: 'sv-ratio--' + area, src: self.images[area + '_' + k], alt: '', loading: 'lazy' }) : null,
            h('span', { class: 'sv-option__name', text: types[k].name }),
            h('span', { class: 'sv-option__desc', text: types[k].short }));
        }))
      );
    });
    return h('div', { class: 'sv-manual' },
      h('h2', { class: 'sv-title sv-title--sm', text: 'Elige tus rasgos' }),
      h('p', { class: 'sv-lead', text: 'Selecciona la opción que más se parezca a ti en cada apartado.' }),
      groups,
      h('div', { class: 'sv-actions' },
        h('button', { class: 'sv-btn sv-btn--primary', type: 'button', onclick: function () {
          self.state.analysis = null; self.state.photoUrl = null; self.state.choice = choice; self.finish();
        } }, 'Ver mis recomendaciones'),
        h('button', { class: 'sv-btn sv-btn--ghost', type: 'button', onclick: function () { self.go('intro'); } }, 'Volver')
      )
    );
  };

  /* ---------- Resultados ---------- */
  App.prototype.finish = function () {
    var em = this.cfg.email || {};
    if (em.enabled !== false && !this.state.emailDone) { this.go('email'); return; }
    this.showResults();
  };

  App.prototype.showResults = function () {
    this.state.tab = 'rostro';
    this.state.fromLink = false;
    this.go('results');
    this.saveToCart();
    this.track();
  };

  /* ---------- Pantalla de email ---------- */
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  App.prototype.view_email = function () {
    var self = this;
    var em = this.cfg.email || {};
    var auto = em.consent !== 'checkbox';
    var input = h('input', { type: 'email', id: 'sv-email', class: 'sv-email__input', name: 'email', autocomplete: 'email', inputmode: 'email', required: true,
      placeholder: em.placeholder || 'Introduce tu email aquí', 'aria-label': 'Tu email', value: this.state.email || this.cfg.customerEmail || '' });
    var consent = auto ? null : h('input', { type: 'checkbox', id: 'sv-email-consent', class: 'sv-email__check' });
    var submit = h('button', { class: 'sv-btn sv-btn--primary sv-email__submit', type: 'submit' }, em.button || 'Enviar');
    var error = h('p', { class: 'sv-email__error', role: 'alert', hidden: true });
    function sync() { submit.disabled = !EMAIL_RE.test(input.value.trim()); error.hidden = true; }
    input.addEventListener('input', sync);
    sync();

    var form = h('form', { class: 'sv-email__form', novalidate: true },
      input,
      consent ? h('label', { class: 'sv-email__consent', for: 'sv-email-consent' }, consent, h('span', { text: em.consentLabel || 'Quiero recibir novedades y ofertas de Saigu Cosmetics por email.' })) : null,
      error,
      submit
    );
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var value = input.value.trim();
      if (!EMAIL_RE.test(value)) { error.textContent = 'Revisa tu email: parece que falta algo.'; error.hidden = false; input.focus(); return; }
      submit.disabled = true;
      submit.textContent = 'Enviando…';
      self.state.email = value;
      var subscribe = auto || (consent && consent.checked);
      self.emit('visagismo_email', { suscrito: subscribe ? 'si' : 'no' });
      // Enviamos a Klaviyo, pero no hacemos esperar a la clienta más de 2,5 s
      var done = false;
      function go() { if (done) return; done = true; self.state.emailDone = true; self.showResults(); self.showToast('Te enviaremos tu resultado a ' + value); }
      self.sendToKlaviyo(value, subscribe).then(go, go);
      setTimeout(go, 2500);
    });

    var legal = em.legal ? h('div', { class: 'sv-email__legal', html: em.legal }) : null;

    return h('div', { class: 'sv-email' + (em.image ? ' has-image' : '') },
      em.image ? h('img', { class: 'sv-email__img', src: em.image, alt: '', 'aria-hidden': 'true' }) : null,
      h('div', { class: 'sv-email__inner' },
        h('h2', { class: 'sv-title', 'data-autofocus': true, text: em.heading || '¿Quieres conocer cómo maquillar tu rostro según tu visagismo?' }),
        h('p', { class: 'sv-lead', text: em.subheading || 'Déjanos tu email y te mandaremos el resultado😉' }),
        form,
        legal,
        h('button', { class: 'sv-email__skip', type: 'button', onclick: function () { self.state.emailDone = true; self.showResults(); } }, em.skip || 'O, si no te interesa, continúa por aquí'),
        h('button', { class: 'sv-email__back', type: 'button', onclick: function () {
          if (self.state.analysis) { self.state.eyeStep = 2; self.go('eyes'); } else self.go('manual');
        } }, '← Volver')
      )
    );
  };

  /* ---------- Klaviyo ---------- */
  // Usa los endpoints públicos de Klaviyo (clave pública / Site ID). Nunca pongas aquí la clave privada.
  var KLAVIYO_REVISION = '2024-10-15';

  App.prototype.resultData = function () {
    var ch = this.state.choice;
    var url = location.origin + location.pathname + '?visagismo=' + ch.rostro + '.' + ch.ojos + '.' + ch.labios;
    return {
      rostro: ch.rostro, rostro_nombre: CONTENT.rostro.types[ch.rostro].name,
      ojos: ch.ojos, ojos_nombre: CONTENT.ojos.types[ch.ojos].name,
      labios: ch.labios, labios_nombre: CONTENT.labios.types[ch.labios].name,
      resumen: this.summary(),
      metodo: this.state.analysis ? 'foto' : 'manual',
      url_resultado: url,
      productos_recomendados: []
    };
  };

  /** Productos del resultado (los de cada paso, en orden y sin repetir), para el email de Klaviyo. */
  App.prototype.recommendedProducts = function () {
    var self = this, ch = this.state.choice;
    var handles = [], seenH = {}, fromCats = [], seenId = {};
    ['rostro', 'ojos', 'labios'].forEach(function (area) {
      CONTENT[area].types[ch[area]].steps.forEach(function (st) {
        if (st.handles && st.handles.length) {
          st.handles.forEach(function (hd) { if (!seenH[hd]) { seenH[hd] = true; handles.push({ h: hd, area: area }); } });
        } else {
          (st.p || []).forEach(function (cat) {
            (self.products[cat] || []).forEach(function (p) { if (!seenId[p.id]) { seenId[p.id] = true; fromCats.push({ p: p, area: area }); } });
          });
        }
      });
    });
    function abs(u) { return !u ? '' : u.indexOf('//') === 0 ? 'https:' + u : u.indexOf('/') === 0 ? location.origin + u : u; }
    function row(p, area) { return { titulo: p.title, url: abs(p.url), imagen: abs(p.image), precio: p.price, zona: area }; }
    return this.fetchProducts(handles.map(function (x) { return x.h; })).then(function (list) {
      var out = [], seen = {};
      list.forEach(function (p) {
        if (seen[p.id]) return; seen[p.id] = true;
        var src = handles.filter(function (x) { return p.url.indexOf('/products/' + x.h) > -1; })[0];
        out.push(row(p, src ? src.area : ''));
      });
      fromCats.forEach(function (x) { if (!seen[x.p.id]) { seen[x.p.id] = true; out.push(row(x.p, x.area)); } });
      return out.slice(0, 12);
    });
  };

  App.prototype.sendToKlaviyo = function (email, subscribe) {
    var self = this;
    var data = this.resultData();
    // Esperamos como mucho 1,5 s a tener los productos; si no, el evento sale sin ellos
    var timeout = new Promise(function (res) { setTimeout(function () { res(null); }, 1500); });
    return Promise.race([this.recommendedProducts().catch(function () { return null; }), timeout]).then(function (prods) {
      if (prods) data.productos_recomendados = prods;
      return self.sendToKlaviyoNow(email, subscribe, data);
    });
  };

  App.prototype.sendToKlaviyoNow = function (email, subscribe, data) {
    var k = this.cfg.klaviyo || {};
    // Si klaviyo.js está en la tienda, identificamos a la clienta en este navegador
    try { if (window.klaviyo && typeof window.klaviyo.identify === 'function') window.klaviyo.identify({ email: email }); } catch (e) {}
    if (!k.publicKey) {
      if (window.console) console.warn('[Visagismo] Falta la clave pública de Klaviyo en los ajustes de la sección');
      return Promise.resolve();
    }
    var base = 'https://a.klaviyo.com/client/';
    var qs = '?company_id=' + encodeURIComponent(k.publicKey);
    var headers = { 'Content-Type': 'application/json', Accept: 'application/json', revision: KLAVIYO_REVISION };
    var profileProps = { visagismo_rostro: data.rostro_nombre, visagismo_ojos: data.ojos_nombre, visagismo_labios: data.labios_nombre, visagismo_url: data.url_resultado };

    var eventReq = fetch(base + 'events/' + qs, {
      method: 'POST', headers: headers,
      body: JSON.stringify({ data: { type: 'event', attributes: {
        properties: data,
        metric: { data: { type: 'metric', attributes: { name: k.metric || 'Test de visagismo completado' } } },
        profile: { data: { type: 'profile', attributes: { email: email, properties: profileProps } } }
      } } })
    });

    var subReq = Promise.resolve();
    if (subscribe && k.listId) {
      subReq = fetch(base + 'subscriptions/' + qs, {
        method: 'POST', headers: headers,
        body: JSON.stringify({ data: {
          type: 'subscription',
          attributes: {
            custom_source: k.source || 'Test de visagismo',
            profile: { data: { type: 'profile', attributes: {
              email: email,
              // Con la revisión 2024-10-15 no se envía «subscriptions»: Klaviyo suscribe
              // el email al marketing por defecto al usar este endpoint.
              properties: profileProps
            } } }
          },
          relationships: { list: { data: { type: 'list', id: k.listId } } }
        } })
      });
    } else if (subscribe && window.console) {
      console.warn('[Visagismo] Falta el ID de la lista de Klaviyo: no se ha suscrito el email');
    }

    function check(label) {
      return function (r) {
        if (r && !r.ok && window.console) r.text().then(function (t) { console.warn('[Visagismo] Klaviyo ' + label + ' respondió ' + r.status, t); });
      };
    }
    return Promise.all([eventReq.then(check('evento')), subReq.then(check('suscripción'))])
      .catch(function (e) { if (window.console) console.warn('[Visagismo] No se pudo conectar con Klaviyo', e); });
  };

  App.prototype.summary = function () {
    var ch = this.state.choice;
    return 'Rostro ' + CONTENT.rostro.types[ch.rostro].name.toLowerCase() +
      ' · Ojos ' + CONTENT.ojos.types[ch.ojos].name.toLowerCase() +
      ' · Labios ' + CONTENT.labios.types[ch.labios].name.toLowerCase();
  };

  App.prototype.view_results = function () {
    var self = this, ch = this.state.choice, a = this.state.analysis;
    var tabs = ['rostro', 'ojos', 'labios'];
    var tabBtns = tabs.map(function (t) {
      return h('button', {
        class: 'sv-tab', type: 'button', role: 'tab', id: 'sv-tab-' + t,
        'aria-selected': self.state.tab === t ? 'true' : 'false', 'aria-controls': 'sv-panel',
        onclick: function () { self.state.tab = t; self.render(); var b = self.root.querySelector('#sv-tab-' + t); if (b) b.focus(); }
      }, h('span', { class: 'sv-tab__area', text: CONTENT[t].label }), h('span', { class: 'sv-tab__type', text: CONTENT[t].types[ch[t]].name }));
    });
    var tablist = h('div', { class: 'sv-tabs', role: 'tablist', 'aria-label': 'Resultados' }, tabBtns);
    tablist.addEventListener('keydown', function (e) {
      var i = tabs.indexOf(self.state.tab);
      if (e.key === 'ArrowRight') i = (i + 1) % 3; else if (e.key === 'ArrowLeft') i = (i + 2) % 3; else return;
      e.preventDefault(); self.state.tab = tabs[i]; self.render(); self.root.querySelector('#sv-tab-' + tabs[i]).focus();
    });

    return h('div', { class: 'sv-results' },
      h('header', { class: 'sv-results__head' },
        this.state.photoUrl ? h('div', { class: 'sv-photo sv-photo--thumb' }, h('img', { src: this.state.photoUrl, alt: 'Tu foto' })) : null,
        h('div', null,
          h('p', { class: 'sv-eyebrow', text: 'Tu visagismo' }),
          h('h2', { class: 'sv-title sv-title--sm', 'data-autofocus': true, text: this.summary() }),
          h('p', { class: 'sv-muted', text: a ? 'Resultado orientativo a partir de tu foto. Si algo no encaja contigo, puedes cambiarlo en cada apartado.' : (this.state.fromLink ? 'Este es el resultado de tu test de visagismo.' : 'Recomendaciones según los rasgos que has elegido.') }),
          h('div', { class: 'sv-actions sv-actions--tight' },
            h('button', { class: 'sv-btn sv-btn--ghost sv-btn--sm', type: 'button', onclick: function () { self.state.analysis = null; self.state.photoUrl = null; self.state.choice = {}; self.go('intro'); } }, 'Repetir el test')
          )
        )
      ),
      tablist,
      h('div', { class: 'sv-panel', id: 'sv-panel', role: 'tabpanel', 'aria-labelledby': 'sv-tab-' + this.state.tab }, this.panel(this.state.tab)),
      this.debug && a ? this.debugBox(a) : null
    );
  };

  App.prototype.panel = function (area) {
    var self = this, ch = this.state.choice, a = this.state.analysis;
    var sec = CONTENT[area], key = ch[area], t = sec.types[key];

    // Rasgos detectados adicionales (ojos y labios pueden tener más de uno)
    var alsoEl = null;
    if (a && area !== 'rostro') {
      var also = a.result[area].filter(function (r) { return r.type !== key && r.score >= THRESH.traitMin; }).slice(0, 2);
      if (also.length) {
        alsoEl = h('p', { class: 'sv-also' }, 'También detectamos rasgos de ',
          also.map(function (r, i) {
            return [i ? ' y ' : '', h('button', { type: 'button', class: 'sv-chip', onclick: function () { ch[area] = r.type; self.render(); self.saveToCart(); }, text: sec.types[r.type].name.toLowerCase() })];
          }).reduce(function (x, y) { return x.concat(y); }, []), '. Toca para ver sus consejos.');
      }
    }

    var selId = 'sv-change-' + area;
    var select = h('select', { id: selId, class: 'sv-select' }, Object.keys(sec.types).map(function (k) {
      return h('option', { value: k, selected: k === key, text: sec.types[k].name });
    }));
    select.addEventListener('change', function () { ch[area] = select.value; self.render(); self.saveToCart(); var s = self.root.querySelector('#' + selId); if (s) s.focus(); });

    var shown = {};
    var steps = t.steps.map(function (st, i) {
      var prodsEl = null;
      if (st.handles && st.handles.length) {
        // Productos concretos elegidos en saigu-visagismo-textos.js: se cargan de la tienda
        prodsEl = h('div', { class: 'sv-products', 'aria-busy': 'true' });
        self.fetchProducts(st.handles).then(function (list) {
          prodsEl.removeAttribute('aria-busy');
          if (!list.length) { prodsEl.hidden = true; return; }
          list.forEach(function (p) { prodsEl.appendChild(self.productCard(p, '')); });
        });
      } else {
        var prods = [];
        (st.p || []).forEach(function (cat) {
          (self.products[cat] || []).forEach(function (p) {
            if (!shown[p.id] && prods.length < 4) { shown[p.id] = true; prods.push({ cat: cat, p: p }); }
          });
        });
        if (prods.length) prodsEl = h('div', { class: 'sv-products' }, prods.map(function (x) { return self.productCard(x.p, x.cat); }));
      }
      return h('li', { class: 'sv-step' },
        h('h4', { class: 'sv-step__q', text: st.q }),
        st.t ? h('p', { text: st.t }) : null,
        st.list ? h('ul', { class: 'sv-step__list' }, st.list.map(function (x) { return h('li', { text: x }); })) : null,
        prodsEl
      );
    });

    var img = this.images[area + '_' + key];
    return h('div', { class: 'sv-panel__inner' },
      h('div', { class: 'sv-type' },
        img ? h('img', { class: 'sv-type__img sv-type__img--' + area, src: img, alt: sec.label + ' ' + t.name.toLowerCase(), loading: 'lazy' }) : null,
        h('div', { class: 'sv-type__copy' },
          h('p', { class: 'sv-eyebrow', text: sec.subtitle }),
          h('h3', { class: 'sv-type__name', text: sec.label + ' ' + t.name.toLowerCase() }),
          h('p', { text: t.intro }),
          alsoEl,
          h('div', { class: 'sv-change' },
            h('label', { for: selId, text: '¿No es tu caso? Cámbialo:' }),
            select)
        )
      ),
      h('ol', { class: 'sv-steps' }, steps)
    );
  };

  /* Carga productos por su handle con la API pública de Shopify (/products/<handle>.js) */
  var productCache = {};
  function formatMoney(cents) {
    var cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';
    try { return new Intl.NumberFormat(document.documentElement.lang || 'es-ES', { style: 'currency', currency: cur }).format(cents / 100); }
    catch (e) { return (cents / 100).toFixed(2) + ' ' + cur; }
  }
  App.prototype.fetchProducts = function (handles) {
    // En la tienda se usa la ruta relativa; en la prueba local, «shopUrl» apunta a la tienda real
    var shop = (this.cfg.shopUrl || '').replace(/\/$/, '');
    var base = shop ? shop + '/' : routes();
    return Promise.all(handles.map(function (handle) {
      if (!productCache[handle]) {
        productCache[handle] = fetch(base + 'products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
          .then(function (d) {
            var v = (d.variants || []).filter(function (x) { return x.available; })[0] || (d.variants || [])[0] || {};
            var img = d.featured_image || (d.images && d.images[0]) || null;
            if (img) img = img + (img.indexOf('?') > -1 ? '&' : '?') + 'width=400';
            return { id: d.id, title: d.title, url: shop + (d.url || ('/products/' + handle)), image: img, price: formatMoney(d.price), variantId: v.id, variants: (d.variants || []).length, available: d.available };
          })
          .catch(function (e) {
            if (window.console) console.warn('[Visagismo] No se encontró el producto «' + handle + '». Revisa el handle en saigu-visagismo-textos.js', e);
            return null;
          });
      }
      return productCache[handle];
    })).then(function (list) { return list.filter(Boolean); });
  };

  App.prototype.productCard = function (p, cat) {
    var self = this;
    var btn;
    if (p.available === false) {
      btn = h('span', { class: 'sv-prod__soldout', text: 'Agotado' });
    } else if (p.variants > 1) {
      btn = h('a', { class: 'sv-btn sv-btn--ghost sv-btn--sm', href: p.url + (p.url.indexOf('?') > -1 ? '&' : '?') + 'utm_source=visagismo', target: '_blank', rel: 'noopener' }, 'Elegir tono');
    } else {
      btn = h('button', { class: 'sv-btn sv-btn--primary sv-btn--sm', type: 'button', onclick: function () { self.addToCart(p, btn); } }, 'Añadir');
    }
    var card = h('article', { class: 'sv-prod' },
      h('a', { class: 'sv-prod__media', href: p.url, target: '_blank', rel: 'noopener', tabindex: '-1', 'aria-hidden': 'true' },
        p.image ? h('img', { src: p.image, alt: '', loading: 'lazy', width: 300, height: 300 }) : null),
      h('div', { class: 'sv-prod__body' },
        (p.label || CATEGORY_LABELS[cat]) ? h('span', { class: 'sv-prod__cat', text: p.label || CATEGORY_LABELS[cat] }) : null,
        h('a', { class: 'sv-prod__title', href: p.url, target: '_blank', rel: 'noopener', text: p.title }),
        p.price ? h('span', { class: 'sv-prod__price', text: p.price }) : null,
        btn)
    );
    card.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('a')) self.emit('visagismo_producto_click', { producto: p.title, zona: self.state.tab });
    });
    return card;
  };

  /* ---------- Carrito y analítica ---------- */
  function routes() { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; }

  App.prototype.addToCart = function (p, btn) {
    var self = this;
    btn.disabled = true;
    fetch(routes() + 'cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: p.variantId, quantity: 1, properties: { _visagismo: this.summary() } }] })
    }).then(function (r) { if (!r.ok) throw r; return r.json(); })
      .then(function () {
        self.showToast('Añadido a tu cesta', routes() + 'cart', 'Ver cesta');
        self.emit('visagismo_anadir_cesta', { producto: p.title, zona: self.state.tab });
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      })
      .catch(function () { self.showToast('No se ha podido añadir. Inténtalo desde la ficha del producto.', p.url, 'Ver producto'); })
      .then(function () { btn.disabled = false; });
  };

  App.prototype.saveToCart = function () {
    if (this.cfg.saveToCart === false || !window.fetch) return;
    fetch(routes() + 'cart/update.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { 'Visagismo': this.summary() } })
    }).catch(function () {});
  };

  /* ---------- Medición ----------
   * Cada paso del embudo se envía a:
   *  · Google Analytics 4 (gtag, si está en la tienda)
   *  · dataLayer (Google Tag Manager)
   *  · Eventos de cliente de Shopify (píxeles personalizados)
   *  · Microsoft Clarity (para filtrar grabaciones)
   * Eventos: visagismo_vista, visagismo_inicio, visagismo_foto_analizada,
   * visagismo_email, visagismo_completado, visagismo_producto_click,
   * visagismo_anadir_cesta, visagismo_resultado_abierto
   */
  App.prototype.emit = function (name, params) {
    params = Object.assign({ test: 'visagismo' }, params || {});
    if (this.debug && window.console) console.log('[Visagismo] evento', name, params);
    if (this.cfg.ga !== false) {
      try { if (typeof window.gtag === 'function') window.gtag('event', name, params); } catch (e) {}
    }
    try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: name }, params)); } catch (e) {}
    try { if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, params); } catch (e) {}
    try { if (typeof window.clarity === 'function') window.clarity('event', name); } catch (e) {}
  };

  App.prototype.track = function () {
    var ch = this.state.choice;
    this.emit('visagismo_completado', { rostro: ch.rostro, ojos: ch.ojos, labios: ch.labios, metodo: this.state.analysis ? 'foto' : 'manual', email: this.state.email ? 'si' : 'no' });
    // Marca a la usuaria en GA4 para poder cruzar sus compras posteriores con el test
    try { if (this.cfg.ga !== false && typeof window.gtag === 'function') window.gtag('set', 'user_properties', { test_visagismo: 'completado' }); } catch (e) {}
    try { if (typeof window.clarity === 'function') window.clarity('set', 'test_visagismo', 'completado'); } catch (e) {}
  };

  App.prototype.started = function (metodo) {
    if (this.state.startedSent) return;
    this.state.startedSent = true;
    this.emit('visagismo_inicio', { metodo: metodo });
  };

  App.prototype.showToast = function (msg, href, linkText) {
    var t = this.toast;
    t.innerHTML = '';
    t.appendChild(h('span', { text: msg }));
    if (href) t.appendChild(h('a', { href: href, text: linkText }));
    t.hidden = false;
    clearTimeout(this._toastT);
    this._toastT = setTimeout(function () { t.hidden = true; }, 5000);
  };

  /* ---------- Depuración / calibración ---------- */
  App.prototype.debugBox = function (a) {
    var m = a.metrics, ans = this.state.eyeAnswers || {};
    var rows = METRICS.map(function (k) {
      return h('tr', null, h('td', { text: k }), h('td', { text: m[k] != null ? m[k].toFixed(3) : '–' }));
    });
    var line = METRICS.map(function (k) { return m[k] != null ? m[k].toFixed(4) : ''; }).join(';') + ';' + (ans.parpado || '') + ';' + (ans.profundidad || '');
    var header = METRICS.join(';') + ';parpado;profundidad';
    var scores = ['rostro', 'ojos', 'labios'].map(function (ar) {
      return h('p', null, h('strong', { text: ar + ': ' }), a.result[ar].map(function (r) { return r.type + ' ' + r.score; }).join(' · '));
    });
    var copy = h('button', { class: 'sv-btn sv-btn--ghost sv-btn--sm', type: 'button', onclick: function () {
      var txt = header + '\n' + line;
      (navigator.clipboard ? navigator.clipboard.writeText(txt) : Promise.reject()).then(function () { copy.textContent = 'Copiado'; }, function () { pre.hidden = false; });
    } }, 'Copiar medidas');
    var pre = h('pre', { hidden: true, text: header + '\n' + line });
    return h('details', { class: 'sv-debug' },
      h('summary', { text: 'Medidas del análisis (modo calibración)' }),
      h('p', { text: 'Pose: giro ' + (a.pose ? a.pose.yaw.toFixed(1) : '–') + '°, inclinación ' + (a.pose ? a.pose.pitch.toFixed(1) : '–') + '°, roll ' + a.roll.toFixed(1) + '°' }),
      scores,
      h('div', { class: 'sv-debug__table' }, h('table', null, h('thead', null, h('tr', null, h('th', { text: 'medida' }), h('th', { text: 'valor' }))), h('tbody', null, rows))),
      copy, pre
    );
  };

  /* ---------- Iconos ---------- */
  function svg(path) {
    var s = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    s.setAttribute('viewBox', '0 0 24 24'); s.setAttribute('aria-hidden', 'true'); s.setAttribute('class', 'sv-icon');
    s.innerHTML = path;
    return s;
  }
  function iconCamera() { return svg('<path d="M4 8h3l2-2.5h6L17 8h3v11H4z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><circle cx="12" cy="13" r="3.6" fill="none" stroke="currentColor" stroke-width="1.6"/>'); }
  function iconUpload() { return svg('<path d="M12 15V4m0 0L7.5 8.5M12 4l4.5 4.5M5 15v4h14v-4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>'); }
  function iconBulb() { return svg('<path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.6 10.8c.7.5 1.1 1.3 1.1 2.2h5c0-.9.4-1.7 1.1-2.2A6 6 0 0 0 12 3z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>'); }
  function iconChevron() { var s = svg('<path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>'); s.setAttribute('class', 'sv-icon sv-chevron'); return s; }
  function iconCheck() { return svg('<path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>'); }
  function iconBack() { return svg('<path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>'); }
  function iconLock() { return svg('<rect x="5" y="10.5" width="14" height="9.5" rx="2" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5" fill="none" stroke="currentColor" stroke-width="1.6"/>'); }

  /* ------------------------------------------------------------------ *
   * 6. ARRANQUE
   * ------------------------------------------------------------------ */
  function boot(scope) {
    (scope || document).querySelectorAll('[data-saigu-visagismo]:not([data-sv-ready])').forEach(function (el) {
      el.setAttribute('data-sv-ready', '');
      el._svApp = new App(el);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();
  // Editor de temas de Shopify: volver a montar al recargar la sección
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target.querySelector('[data-saigu-visagismo]');
    if (el && el._svApp) el._svApp.stopCamera();
  });

  window.SaiguVisagismo = { boot: boot, classify: classify, measure: measure, normalize: normalize, applyEyeAnswers: applyEyeAnswers, RULES: RULES, THRESH: THRESH, CONTENT: CONTENT };
})();